import { getSessionToken } from './sessionTokenService';

export interface UserProfile {
  id: number;
  email: string;
  hasPassword: boolean;
  hasGoogleId: boolean;
  hasProSubscription?: boolean;
  proSubscriptionEndDate?: string;
  /** When the user first used the 14-day free trial; null/undefined if never used */
  freeTrialUsedAt?: string | null;
  /** When true, user is blocked from subscribing (e.g. abuse pattern) */
  disallowProSubscription?: boolean;
}

export interface ChangePasswordRequest {
  oldPassword: string;
  newPassword: string;
}

export interface ChangePasswordResponse {
  message: string;
}

export interface DeleteAccountRequest {
  password?: string;
  email?: string;
}

export interface DeleteAccountResponse {
  message: string;
}

class AuthApiService {
  private baseUrl = `${process.env.REACT_APP_API_URL || ''}/api/v1/auth`;

  private async makeRequest<T>(url: string, options: RequestInit = {}): Promise<T> {
    const token = localStorage.getItem('accessToken');
    if (!token) {
      throw new Error('No access token found');
    }

    // Add session token to all requests
    const sessionToken = getSessionToken();
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      ...(options.headers as Record<string, string>),
    };
    if (sessionToken) {
      headers['X-Session-Token'] = sessionToken;
    }

    const response = await fetch(url, {
      ...options,
      headers,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    return response.json();
  }

  // Get user profile
  async getProfile(): Promise<UserProfile> {
    return this.makeRequest<UserProfile>(`${this.baseUrl}/profile`);
  }

  // Change password
  async changePassword(data: ChangePasswordRequest): Promise<ChangePasswordResponse> {
    return this.makeRequest<ChangePasswordResponse>(`${this.baseUrl}/change-password`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Delete account
  async deleteAccount(data: DeleteAccountRequest): Promise<DeleteAccountResponse> {
    return this.makeRequest<DeleteAccountResponse>(`${this.baseUrl}/account`, {
      method: 'DELETE',
      body: JSON.stringify(data),
    });
  }
}

export const authApi = new AuthApiService();

